import React, { useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Layout,
  Card,
  Button,
  Table,
  Skeleton,
  Switch,
  Tag,
  Alert,
  Descriptions,
  Empty,
  List,
} from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import { Get } from "../../config/api/get";
import { Put } from "../../config/api/put";
import { ASSOCIATE } from "../../config/constants/api";
import "../upcoming-lessons/upcoming-lessons.css";
import "../course-management/course-management.css";

const STATUS_COLORS = {
  PENDING: "gold",
  ACTIVE: "green",
  INACTIVE: "red",
};

function AssociateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const user = useSelector((state) => state.user.userData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [associate, setAssociate] = useState(null);
  const [courses, setCourses] = useState([]);

  const canManage = Boolean(user?.hasTrainingAssociates);
  const canAssignCourses = associate?.status === "ACTIVE";

  const coursesPath = `${ASSOCIATE.courses}${id}/courses`;

  useEffect(() => {
    if (!token) {
      navigate("/signin");
      return;
    }
    if (!canManage) {
      setLoading(false);
      return;
    }
    loadCourseAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, canManage]);

  const loadCourseAccess = async () => {
    setLoading(true);
    try {
      const detailRes = await Get(ASSOCIATE.get + id, token);
      if (!detailRes?.status) {
        swal("Oops!", detailRes?.message || "Associate not found", "error");
        navigate("/associate-management");
        return;
      }

      const associateDoc = detailRes?.data?.associate || null;
      setAssociate(associateDoc);

      if (associateDoc?.status === "ACTIVE") {
        const coursesRes = await Get(coursesPath, token);
        if (coursesRes?.status) {
          setCourses(coursesRes?.data?.courses || []);
        } else {
          swal("Oops!", coursesRes?.message || "Failed to load courses", "error");
          setCourses([]);
        }
      } else {
        setCourses([]);
      }
    } catch (error) {
      swal("Oops!", error?.message || "Failed to load associate", "error");
      navigate("/associate-management");
    } finally {
      setLoading(false);
    }
  };

  const assignedCourses = useMemo(
    () => courses.filter((c) => c.granted),
    [courses]
  );

  const toggleCourse = (courseId, granted) => {
    setCourses((prev) =>
      prev.map((c) =>
        String(c._id) === String(courseId) ? { ...c, granted } : c
      )
    );
  };

  const handleSave = async () => {
    if (!canAssignCourses) {
      swal(
        "Not available",
        "Course access can only be managed for approved (active) associates.",
        "info"
      );
      return;
    }

    const courseIds = courses.filter((c) => c.granted).map((c) => c._id);
    setSaving(true);
    try {
      const response = await Put(coursesPath, token, { courseIds });
      if (response?.status) {
        swal(
          "Success!",
          response?.message || "Course access updated successfully",
          "success"
        );
        loadCourseAccess();
      } else {
        swal("Oops!", response?.message || "Failed to update courses", "error");
      }
    } catch (error) {
      swal("Oops!", error?.message || "Failed to update courses", "error");
    } finally {
      setSaving(false);
    }
  };

  const courseColumns = [
    {
      title: "S. No.",
      key: "index",
      width: 80,
      render: (_, __, index) => (
        <span>{(index < 9 ? "0" : "") + (index + 1)}</span>
      ),
    },
    {
      title: "Course code",
      dataIndex: "courseCode",
      key: "courseCode",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag>{status}</Tag>,
    },
    {
      title: "Access",
      key: "access",
      width: 100,
      render: (_, record) => (
        <Switch
          checked={record.granted}
          disabled={!canAssignCourses}
          onChange={(checked) => toggleCourse(record._id, checked)}
        />
      ),
    },
  ];

  if (!canManage) {
    return (
      <Layout className="configuration" style={{ minHeight: "100vh" }}>
        <Row style={{ justifyContent: "center", padding: 48 }}>
          <Card>
            <Typography.Paragraph>
              Training associates are not enabled on your account.
            </Typography.Paragraph>
            <Button type="primary" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </Card>
        </Row>
      </Layout>
    );
  }

  const associateName = associate
    ? `${associate.firstName} ${associate.lastName}`
    : "Associate";

  return (
    <Layout
      className="configuration upcoming-lessons-page"
      style={{ minHeight: "100vh" }}
    >
      <Row className="blueBackground-old coursebnr">
        <Typography.Title
          level={1}
          className="fontFamily1 aboutbanner-content upcoming-lessons-hero-title"
        >
          Associate details
        </Typography.Title>
      </Row>

      <div className="boxDetails">
        <Row
          className="whiteBackground upcoming-lessons-section"
          style={{ justifyContent: "center" }}
        >
          <Col xs={24} lg={22} xl={21}>
            <div className="upcoming-lessons-inner">
              <Card className="infoBox upcoming-lessons-card" bordered={false}>
                <Row className="upcoming-lessons-toolbar" gutter={[16, 16]}>
                  <Col xs={24} md={14}>
                    <div className="course-management-title-block">
                      <button
                        type="button"
                        className="course-management-inline-back"
                        aria-label="Go back"
                        onClick={() => navigate("/associate-management")}
                      >
                        <FaArrowLeft aria-hidden="true" />
                      </button>
                      <Typography.Title
                        level={2}
                        className="fontFamily1 course-management-page-title"
                      >
                        {associateName}
                      </Typography.Title>
                    </div>
                  </Col>
                  <Col xs={24} md={10} className="quiz-management-actions-col">
                    {canAssignCourses ? (
                      <div className="course-management-primary-actions">
                        <Button
                          type="primary"
                          shape="round"
                          size="large"
                          className="loginButton"
                          loading={saving}
                          onClick={handleSave}
                        >
                          Save course access
                        </Button>
                      </div>
                    ) : null}
                  </Col>
                </Row>

                {loading ? (
                  <Skeleton active />
                ) : (
                  <>
                    {associate ? (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2 }}
                        style={{ marginBottom: 24 }}
                      >
                        <Descriptions.Item label="Email">
                          {associate.email}
                        </Descriptions.Item>
                        <Descriptions.Item label="Position">
                          {associate.position}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                          <Tag color={STATUS_COLORS[associate.status]}>
                            {associate.status}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    ) : null}

                    {!canAssignCourses ? (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                        message="Course assignment unavailable"
                        description="Assign and unassign courses after admin approves this associate (status ACTIVE)."
                      />
                    ) : null}

                    <Typography.Title level={4} style={{ margin: "10px 10px" }}>
                      Assigned courses ({assignedCourses.length})
                    </Typography.Title>
                    {assignedCourses.length === 0 ? (
                      <Empty
                        description="No courses assigned yet"
                        style={{ marginBottom: 24 }}
                      />
                    ) : (
                      <List
                        bordered
                        size="small"
                        style={{ marginBottom: 32 }}
                        dataSource={assignedCourses}
                        renderItem={(course, index) => (
                          <List.Item>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 12,
                                width: "100%",
                              }}
                            >
                              <Typography.Text
                                strong
                                style={{
                                  minWidth: 28,
                                  color: "#2484c6",
                                }}
                              >
                                {(index + 1).toString().padStart(2, "0")}.
                              </Typography.Text>
                              <div style={{ flex: 1 }}>
                                <Typography.Text strong>
                                  {course.title}
                                </Typography.Text>
                                {course.courseCode ? (
                                  <div>
                                    <Typography.Text type="secondary">
                                      {course.courseCode}
                                    </Typography.Text>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />
                    )}

                    <Typography.Title level={4} style={{ margin: "10px 10px" }}>
                      All your courses
                    </Typography.Title>
                    {courses.length === 0 ? (
                      <Empty description="You have no courses to assign" />
                    ) : (
                      <Table
                        rowKey="_id"
                        columns={courseColumns}
                        dataSource={courses}
                        pagination={false}
                        className="styledTable2"
                      />
                    )}
                  </>
                )}
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    </Layout>
  );
}

export default AssociateDetails;
